import { X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUser } from '@/features/plan/hooks/use-plan-data'
import { sendSupportTicket } from '@/features/support/support-service'
import { useSupportChatEligible } from '@/features/support/use-support-chat-eligible'
import { Button } from '@/shared/components/button'
import { MessageIcon } from '@/shared/components/icons/message'
import { cn } from '@/shared/lib/utils'

type Status = 'idle' | 'loading' | 'success' | 'error'

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function HelpPopup() {
  const supportChatActive = useSupportChatEligible()
  const { t } = useTranslation()
  const { data: userData } = useUser()
  const [isOpen, setIsOpen] = useState(false)
  const [emailValue, setEmailValue] = useState('')
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailInit, setEmailInit] = useState(false)

  const userName = userData?.me.name ?? ''

  // Pre-fill email from user data once
  if (userData?.me.email && !emailInit) {
    setEmailValue(userData.me.email)
    setEmailInit(true)
  }

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setTimeout(() => {
      setMessage('')
      setStatus('idle')
      setEmailTouched(false)
    }, 300)
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setEmailTouched(true)
      if (!message.trim() || !isValidEmail(emailValue)) return

      setStatus('loading')
      const success = await sendSupportTicket({
        name: userName,
        email: emailValue,
        message: message.trim(),
      })

      if (success) {
        setStatus('success')
        setMessage('')
        setTimeout(handleClose, 5000)
      } else {
        setStatus('error')
      }
    },
    [message, emailValue, userName, handleClose],
  )

  // Hide when the Zendesk-backed support widget (embed.js bootstrap) is
  // active — we don't want two floating "contact us" entry points on
  // screen at once.
  if (supportChatActive) return null

  return (
    <>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="fixed right-6 bottom-6 z-[60] flex cursor-pointer items-center gap-2 rounded-full bg-app-primary-600 px-5 py-2.5 font-inter text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:brightness-110 active:scale-95"
      >
        <MessageIcon className="size-4" />
        <span>
          {isOpen
            ? t('popup_help_close_button_web')
            : t('popup_help_button_title_web')}
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[60] bg-black/20"
              onClick={handleClose}
            />

            {/* Popup */}
            <motion.div
              key="popup"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{
                duration: 0.3,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="fixed right-6 bottom-20 z-[61] flex w-[360px] flex-col overflow-hidden rounded-[24px] border border-app-grey-200 bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-4">
                <h3 className="font-inter text-lg font-semibold text-app-grey-900">
                  {t('popup_help_title_web')}
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex size-8 cursor-pointer items-center justify-center rounded-full bg-app-grey-100 transition-colors hover:bg-app-grey-200"
                >
                  <X className="size-4 text-app-grey-500" />
                </button>
              </div>

              {status === 'success' ? (
                <div
                  className="flex flex-1 flex-col items-center justify-center gap-3 px-6 pb-6"
                  style={{ minHeight: 352 }}
                >
                  <h2 className="text-center font-display text-2xl font-medium text-app-primary-900">
                    {t('contact_support_success_title')}
                  </h2>
                  <p className="text-center font-inter text-sm leading-5 text-app-grey-600">
                    {t('contact_support_success_body')}
                  </p>
                </div>
              ) : (
                <form
                  className="flex flex-col gap-1.5 px-6 pb-6"
                  onSubmit={handleSubmit}
                >
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="help-email"
                      className="font-inter text-sm font-medium text-app-grey-900"
                    >
                      {t('contact_support_email_label')}
                    </label>
                    <input
                      type="email"
                      id="help-email"
                      value={emailValue}
                      onChange={(e) => {
                        setEmailValue(e.target.value)
                        setEmailTouched(true)
                      }}
                      onBlur={() => setEmailTouched(true)}
                      placeholder={t('contact_support_email_placeholder')}
                      className={cn(
                        'rounded-[10px] border px-4 py-3 font-inter text-sm text-app-grey-900 outline-none transition-colors placeholder:text-app-grey-400 focus:border-app-primary-600',
                        emailTouched && !isValidEmail(emailValue)
                          ? 'border-red-500'
                          : 'border-app-grey-200',
                      )}
                    />
                    <p className="min-h-[16px] font-inter text-xs text-red-500">
                      {emailTouched && !isValidEmail(emailValue)
                        ? t('contact_support_email_invalid_error')
                        : ''}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor="help-message"
                      className="font-inter text-sm font-medium text-app-grey-900"
                    >
                      {t('contact_support_message_label')}
                    </label>
                    <textarea
                      id="help-message"
                      name="message"
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder={t('contact_support_message_placeholder')}
                      className="h-[120px] resize-none rounded-[10px] border border-app-grey-200 px-4 py-3 font-inter text-sm text-app-grey-900 outline-none transition-colors placeholder:text-app-grey-400 focus:border-app-primary-600"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    loading={status === 'loading'}
                    className="mt-5"
                  >
                    {t('contact_support_submit_button')}
                  </Button>
                  {status === 'error' && (
                    <p className="text-center font-inter text-xs text-red-500">
                      {t('contact_support_failure_message')}
                    </p>
                  )}
                </form>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  )
}
